import type { MeProfile } from '@/api/services/me';
import { formatPlanDisplayName } from '@/utils/planDisplayName';
import { isPlatformStaffRole, normalizePlatformRole } from '@/utils/teamRoles';
import { workspaceRoleLabel } from '@/utils/workspaceRoles';
import { workspacePlanSlug } from '@/utils/capabilities';

export type SidebarIdentityChip = {
  key: string;
  variant: 'role' | 'workspace' | 'plan' | 'status' | 'warning';
  label: string;
};

export type SidebarIdentityModel = {
  displayName: string;
  contextLine: string;
  chips: SidebarIdentityChip[];
  showReadOnly: boolean;
};

type WorkspaceContext = {
  isPersonalMode: boolean;
  activeWorkspaceName: string | null;
  canSwitchWorkspace: boolean;
};

function resolveRoleSubtitle(me: MeProfile, isPlatformStaff: boolean, isPersonal: boolean): string {
  if (isPlatformStaff) {
    const role = normalizePlatformRole(me.role ?? '');
    if (role === 'owner') return 'Platform owner';
    if (role === 'platform_admin') return 'Platform admin';
    if (role === 'platform_support') return 'Platform support';
    return 'Platform access';
  }

  if (me.permissions.readOnly || (me.role ?? '').trim().toLowerCase() === 'viewer') {
    return 'Viewer';
  }

  const membershipRole = (me.membershipRole ?? me.role ?? '').trim().toLowerCase();
  if (membershipRole === 'workspace_admin' || membershipRole === 'organization_admin' || membershipRole === 'customer_admin') {
    return 'Workspace admin';
  }
  if (membershipRole === 'viewer') {
    return 'Viewer';
  }
  if (isPersonal) {
    return 'Personal workspace';
  }
  return 'Workspace member';
}

export function buildSidebarIdentity(
  me: MeProfile,
  workspace: WorkspaceContext,
): SidebarIdentityModel {
  const displayName = String(me.name || me.email || 'User').trim();
  const isPlatformStaff = isPlatformStaffRole(me.role ?? '');
  const isPersonal = workspace.isPersonalMode;
  const contextLine = resolveRoleSubtitle(me, isPlatformStaff, isPersonal);

  const chips: SidebarIdentityChip[] = [];

  if (isPlatformStaff) {
    const role = normalizePlatformRole(me.role ?? '');
    chips.push({
      key: 'role',
      variant: 'role',
      label: role === 'owner' ? 'Owner' : role === 'platform_admin' ? 'Platform Admin' : role === 'platform_support' ? 'Platform Support' : 'Staff',
    });
    if (workspace.canSwitchWorkspace) {
      chips.push({
        key: 'workspace',
        variant: 'workspace',
        label: isPersonal ? 'Personal' : 'All workspaces',
      });
    }
  } else {
    const membershipRole = me.membershipRole ?? me.role ?? 'subscriber';
    chips.push({
      key: 'role',
      variant: 'role',
      label: workspaceRoleLabel(membershipRole),
    });
    chips.push({
      key: 'workspace',
      variant: 'workspace',
      label: isPersonal ? 'Personal' : 'Organization',
    });
  }

  const planChipLabel = resolveSidebarPlanChipLabel(me);
  if (planChipLabel) {
    chips.push({
      key: 'plan',
      variant: 'plan',
      label: planChipLabel,
    });
  }

  return {
    displayName,
    contextLine,
    chips,
    showReadOnly: Boolean(me.permissions.readOnly),
  };
}

export function resolveSidebarPlanChipLabel(me: MeProfile | null | undefined): string | null {
  if (!me) return null;
  if (me.billingBypass || isPlatformStaffRole(me.role ?? '')) {
    return null;
  }
  const slug = workspacePlanSlug(me);
  if (!slug) return null;
  const normalized = String(slug).trim().toLowerCase();
  if (!normalized || normalized === 'n/a' || normalized === 'na') return null;
  const label = formatPlanDisplayName(slug);
  if (!label || label === '—' || label.toLowerCase() === 'n/a') return null;
  return label;
}
